import { useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import {
  Instagram, Loader2, Settings, ChevronDown, RotateCcw, Trash2, Send,
  ImageOff, Heart, MessageCircle, ExternalLink, CheckCircle2,
  Sparkles, Calendar, CheckCircle, X, Zap, Eye,
  BarChart3, ShieldAlert, Star, GraduationCap, FileText,
  LayoutGrid, Image, Quote, ArrowLeftRight, Info,
  Type, GitBranch, ListChecks, ArrowLeft,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
// Layout wrapper placeholder — each project wraps differently.
// The install.sh replaces __LAYOUT_OPEN__ and __LAYOUT_CLOSE__ below.
import { format, formatDistanceToNow, startOfDay } from "date-fns";
import { fr } from "date-fns/locale";
import { useIgQueue, type IgQueuePost } from "@/hooks/useIgQueue";
import { useIgPublished, type IgPublishedPost } from "@/hooks/useIgPublished";
import { useIgProject } from "@/hooks/useIgProject";

// ─── Constants ────────────────────────────────────────────────────────────────

const IGGradient = "linear-gradient(135deg,#833ab4,#fd1d1d,#fcb045)";

function useProjectSlug(): string {
  const params = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
  return params.get("project") || "piktable";
}

const CAT_CFG: Record<string, { label: string; cls: string }> = {
  content:      { label: "Contenu",   cls: "bg-slate-500/10 text-slate-600" },
  pain_point:   { label: "Pain",      cls: "bg-red-500/10 text-red-600" },
  feature:      { label: "Feature",   cls: "bg-blue-500/10 text-blue-600" },
  social_proof: { label: "Social",    cls: "bg-yellow-500/10 text-yellow-700" },
  educational:  { label: "Éducation", cls: "bg-green-500/10 text-green-600" },
};

// ─── Shared helpers ───────────────────────────────────────────────────────────

function CBadge({ c }: { c: string }) {
  const cfg = CAT_CFG[c] || { label: c, cls: "bg-muted text-muted-foreground" };
  return <Badge variant="outline" className={`text-xs ${cfg.cls}`}>{cfg.label}</Badge>;
}

function Thumb({ url, onClick }: { url: string | null; onClick?: () => void }) {
  const inner = url
    ? <img src={url} alt="" className="w-full h-full object-cover" />
    : <ImageOff className="h-4 w-4 text-muted-foreground" />;
  const cls = "relative w-12 h-12 rounded-lg overflow-hidden border border-border flex-shrink-0 bg-muted flex items-center justify-center";
  return onClick
    ? <button onClick={onClick} className={`${cls} hover:opacity-80 transition-opacity`}>{inner}</button>
    : <div className={cls}>{inner}</div>;
}

function CarouselViewer({ imageUrl, imageUrls }: { imageUrl: string | null; imageUrls?: string[] }) {
  const urls = Array.isArray(imageUrls) && imageUrls.length >= 2 ? imageUrls : imageUrl ? [imageUrl] : [];
  const [idx, setIdx] = useState(0);
  const isCarousel = urls.length >= 2;

  // Reset index when slides change (switching between posts)
  const urlsKey = urls.join(",");
  const prevUrlsKey = useRef(urlsKey);
  if (urlsKey !== prevUrlsKey.current) { prevUrlsKey.current = urlsKey; if (idx !== 0) setIdx(0); }

  if (urls.length === 0) {
    return (
      <div className="w-full sm:w-1/2 flex-shrink-0 bg-muted flex items-center justify-center" style={{ aspectRatio: "1/1" }}>
        <ImageOff className="h-12 w-12 text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="w-full sm:w-1/2 flex-shrink-0 bg-muted relative">
      <img src={urls[idx]} alt="" className="w-full h-auto object-contain" style={{ aspectRatio: "1/1" }} />
      {isCarousel && (
        <>
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
            {urls.map((_, i) => (
              <button key={i} onClick={() => setIdx(i)}
                className={`w-2 h-2 rounded-full transition-all ${i === idx ? "bg-white scale-125" : "bg-white/50"}`} />
            ))}
          </div>
          {idx > 0 && (
            <button onClick={() => setIdx(idx - 1)} className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/40 text-white flex items-center justify-center hover:bg-black/60">
              <ChevronDown className="h-4 w-4 -rotate-90" />
            </button>
          )}
          {idx < urls.length - 1 && (
            <button onClick={() => setIdx(idx + 1)} className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/40 text-white flex items-center justify-center hover:bg-black/60">
              <ChevronDown className="h-4 w-4 rotate-90" />
            </button>
          )}
          <Badge className="absolute top-3 right-3 bg-black/50 text-white text-[10px]">{idx + 1}/{urls.length}</Badge>
        </>
      )}
    </div>
  );
}

function PlatResult({ label, ok }: { label: string; ok: boolean }) {
  return (
    <span className={`text-xs font-semibold ${ok ? "text-emerald-600" : "text-red-500"}`}>
      {label} {ok ? "✅" : "❌"}
    </span>
  );
}

function getHeadline(post: IgQueuePost | IgPublishedPost): string {
  const meta = (post as IgQueuePost).generation_metadata;
  if (meta && typeof meta === "object" && "headline" in meta && typeof meta.headline === "string") {
    return meta.headline;
  }
  return post.template_id || post.category || "—";
}

// ─── Post Detail Dialog ───────────────────────────────────────────────────────

interface PostDetailDialogProps {
  post: IgQueuePost | null;
  publishedPost: IgPublishedPost | null;
  onClose: () => void;
  onCaptionSave?: (id: string, cap: string) => Promise<boolean>;
  onPublish?: (id: string) => Promise<unknown>;
  onRegenerate?: (p: IgQueuePost) => Promise<unknown>;
  onDelete?: (id: string) => Promise<boolean>;
  onSchedule?: (postId: string) => Promise<void>;
  onUnschedule?: (postId: string) => Promise<void>;
  publishTo?: string[];
}

function PostDetailDialog({
  post, publishedPost, onClose, onCaptionSave, onPublish,
  onRegenerate, onDelete, onSchedule, onUnschedule, publishTo = ["instagram"],
}: PostDetailDialogProps) {
  const [caption, setCaption] = useState("");
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [done, setDone] = useState(false);
  const [link, setLink] = useState<string | null>(null);

  const isOpen = !!(post || publishedPost);

  // Reset state when a different post is opened
  const currentId = post?.id || publishedPost?.id || null;
  const prevIdRef = useRef<string | null>(null);
  if (currentId && currentId !== prevIdRef.current) {
    prevIdRef.current = currentId;
    // Can't call setState during render in strict mode, but this pattern
    // is safe because it only runs when the ID actually changes
    const newCaption = post?.caption || publishedPost?.caption || "";
    if (caption !== newCaption) setCaption(newCaption);
    if (done) setDone(false);
    if (link) setLink(null);
    if (publishing) setPublishing(false);
    if (saving) setSaving(false);
  }
  const imageUrl = post?.image_url || publishedPost?.image_url || null;
  const isPublished = !!publishedPost;
  const isScheduled = !!(post?.scheduled_at);

  const handlePublish = async () => {
    if (!post || !onPublish) return;
    setPublishing(true);
    const r = await onPublish(post.id) as { ig_permalink?: string } | null;
    setPublishing(false);
    setDone(true);
    if (r && "ig_permalink" in r) setLink(r.ig_permalink || null);
    setTimeout(onClose, 2500);
  };

  const handleSave = async () => {
    if (!post || !onCaptionSave) return;
    setSaving(true);
    const ok = await onCaptionSave(post.id, caption);
    setSaving(false);
    if (ok) toast.success("Légende enregistrée");
    else toast.error("Erreur lors de l'enregistrement");
  };

  const handleDelete = async () => {
    if (!post || !onDelete) return;
    if (!window.confirm("Supprimer ce post ? Cette action est irréversible.")) return;
    await onDelete(post.id);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl p-0">
        <div className="flex flex-col sm:flex-row max-h-[85vh]">
          <CarouselViewer
            imageUrl={imageUrl}
            imageUrls={Array.isArray(post?.image_urls) ? post.image_urls : Array.isArray(publishedPost?.image_urls) ? publishedPost.image_urls : []}
          />
          <div className="flex-1 p-5 flex flex-col gap-3 overflow-y-auto">
            <DialogHeader><DialogTitle className="text-base">Détail du post</DialogTitle></DialogHeader>

            {/* Badges */}
            <div className="flex flex-wrap gap-1.5">
              <CBadge c={post?.category || publishedPost?.category || ""} />
              {(post?.template_id || publishedPost?.template_id) && (
                <Badge variant="outline" className="text-xs font-mono">{post?.template_id || publishedPost?.template_id}</Badge>
              )}
              {isPublished && publishedPost?.published_at && (
                <Badge variant="outline" className="text-xs text-emerald-600">
                  Publié le {format(new Date(publishedPost.published_at), "d MMM à H'h'mm", { locale: fr })}
                </Badge>
              )}
              {isScheduled && post?.scheduled_at && (
                <Badge variant="outline" className="text-xs text-blue-600">
                  Planifié {format(new Date(post.scheduled_at), "EEE d MMM à H'h'mm", { locale: fr })}
                </Badge>
              )}
              {post?.created_at && (
                <Badge variant="outline" className="text-[10px] text-muted-foreground">
                  Généré {format(new Date(post.created_at), "d MMM H'h'mm", { locale: fr })}
                </Badge>
              )}
            </div>

            {/* Platform results for published */}
            {isPublished && (
              <div className="flex gap-3">
                {publishTo.includes("instagram") && (
                  <PlatResult label="IG" ok={!!publishedPost?.ig_media_id} />
                )}
                {publishTo.includes("facebook") && (
                  <PlatResult label="FB" ok={!!publishedPost?.fb_post_id} />
                )}
              </div>
            )}

            {/* Engagement for published */}
            {isPublished && publishedPost?.ig_analytics && (
              <div className="flex items-center gap-3 text-sm">
                <span className="flex items-center gap-1"><Heart className="h-4 w-4 text-red-400" />{publishedPost.ig_analytics.likes ?? 0}</span>
                <span className="flex items-center gap-1"><MessageCircle className="h-4 w-4 text-blue-400" />{publishedPost.ig_analytics.comments ?? 0}</span>
                <Badge variant="outline" className="text-xs text-emerald-600">{(publishedPost.ig_analytics.engagement_score ?? 0).toFixed(1)}%</Badge>
              </div>
            )}

            {/* Generation metadata */}
            {post?.generation_metadata && (() => {
              const meta = post.generation_metadata as Record<string, unknown>;
              return (meta.composition || meta.visual_direction) ? (
                <div className="flex flex-wrap gap-1.5">
                  {meta.composition && <Badge variant="secondary" className="text-[10px]">{String(meta.composition)}</Badge>}
                  {meta.visual_direction && <Badge variant="secondary" className="text-[10px]">{String(meta.visual_direction)}</Badge>}
                  {meta.visual_style && <Badge variant="secondary" className="text-[10px]">{String(meta.visual_style)}</Badge>}
                  {meta.slide_count && Number(meta.slide_count) > 1 && <Badge variant="secondary" className="text-[10px]">{String(meta.slide_count)} slides</Badge>}
                </div>
              ) : null;
            })()}

            {/* Caption */}
            <div className="flex flex-col gap-1">
              <Label className="text-xs">Légende</Label>
              {isPublished
                ? <div className="text-sm text-muted-foreground rounded-md border p-2 bg-muted/30 max-h-32 overflow-y-auto whitespace-pre-wrap">{caption || <span className="italic text-muted-foreground/50">Aucune légende</span>}</div>
                : <Textarea value={caption} onChange={(e) => setCaption(e.target.value)} className="text-sm resize-none min-h-[100px]" placeholder="Écrire une légende..." />
              }
            </div>

            {/* Hashtags */}
            {(() => {
              const tags = post?.hashtags || publishedPost?.hashtags;
              return Array.isArray(tags) && tags.length > 0 ? (
                <div className="flex flex-col gap-1">
                  <Label className="text-xs">Hashtags ({tags.length})</Label>
                  <div className="max-h-20 overflow-y-auto rounded-md border p-2 bg-muted/30 text-xs text-muted-foreground leading-relaxed">
                    {tags.join(" ")}
                  </div>
                </div>
              ) : null;
            })()}

            {/* Actions */}
            <div className="flex flex-col gap-2 mt-auto">
              {isPublished && publishedPost?.ig_permalink && (
                <a href={publishedPost.ig_permalink} target="_blank" rel="noreferrer">
                  <Button variant="outline" className="w-full gap-2"><ExternalLink className="h-4 w-4" />Voir sur Instagram</Button>
                </a>
              )}
              {!isPublished && post && (
                <>
                  {done ? (
                    <div className="flex flex-col items-center gap-2 py-3">
                      <CheckCircle2 className="h-8 w-8 text-emerald-500" />
                      <p className="text-sm font-semibold text-emerald-600">Publié avec succès !</p>
                      {link && <a href={link} target="_blank" rel="noreferrer" className="text-xs text-blue-600 underline flex items-center gap-1">Voir sur Instagram <ExternalLink className="h-3 w-3" /></a>}
                    </div>
                  ) : (
                    <Button onClick={handlePublish} disabled={publishing} className="w-full text-white font-semibold" style={{ background: IGGradient }}>
                      {publishing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                      Publier maintenant
                    </Button>
                  )}
                  <div className="flex gap-2">
                    {!isScheduled && onSchedule && (
                      <Button variant="outline" size="sm" className="flex-1 text-blue-600" onClick={async () => { await onSchedule(post.id); onClose(); }}>
                        <Calendar className="h-3.5 w-3.5 mr-1" />Planifier
                      </Button>
                    )}
                    {isScheduled && onUnschedule && (
                      <Button variant="outline" size="sm" className="flex-1" onClick={async () => { await onUnschedule(post.id); onClose(); }}>
                        <X className="h-3.5 w-3.5 mr-1" />Dé-planifier
                      </Button>
                    )}
                    <Button variant="outline" size="sm" className="flex-1" onClick={handleSave} disabled={saving}>
                      {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Enregistrer"}
                    </Button>
                    {onRegenerate && (
                      <Button variant="outline" size="sm" onClick={() => onRegenerate(post)}><RotateCcw className="h-3.5 w-3.5" /></Button>
                    )}
                    {onDelete && (
                      <Button variant="outline" size="sm" onClick={handleDelete} className="text-destructive hover:text-destructive">
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Automation constants ─────────────────────────────────────────────────────

const PIPELINE_STEPS = [
  { key: "auto_generate", label: "Auto-generate", desc: "Génère les posts selon vos paramètres", icon: Sparkles, color: "text-purple-500" },
  { key: "auto_schedule", label: "Auto-schedule", desc: "Place les posts dans le calendrier", icon: Calendar, color: "text-blue-500" },
  { key: "auto_publish", label: "Auto-publish", desc: "Publie aux créneaux planifiés", icon: Send, color: "text-emerald-500" },
] as const;

const CONTENT_VARIANTS = [
  { key: "content", label: "Contenu", desc: "Présentation générale du produit/service", icon: FileText, color: "bg-slate-500/10 text-slate-600" },
  { key: "pain_point", label: "Pain points", desc: "Problèmes concrets du client cible", icon: ShieldAlert, color: "bg-red-500/10 text-red-600" },
  { key: "feature", label: "Features", desc: "Mise en avant de fonctionnalités", icon: Zap, color: "bg-blue-500/10 text-blue-600" },
  { key: "social_proof", label: "Social proof", desc: "Témoignages, chiffres, résultats", icon: Star, color: "bg-yellow-500/10 text-yellow-700" },
  { key: "educational", label: "Éducation", desc: "Tips, tutoriels, vulgarisation", icon: GraduationCap, color: "bg-green-500/10 text-green-600" },
] as const;

const VISUAL_FORMATS_PHOTO = [
  { key: "simple", label: "Visuel simple", desc: "Image + texte overlay", icon: Image, color: "bg-cyan-500/10 text-cyan-600" },
  { key: "citation", label: "Citation", desc: "Quote card avec grande guillemet", icon: Quote, color: "bg-pink-500/10 text-pink-600" },
  { key: "before_after", label: "Avant / Après", desc: "Comparaison visuelle 2 colonnes", icon: ArrowLeftRight, color: "bg-amber-500/10 text-amber-700" },
  { key: "carousel", label: "Carrousel", desc: "Multi-slides storytelling", icon: LayoutGrid, color: "bg-orange-500/10 text-orange-600" },
] as const;

const VISUAL_FORMATS_GRAPHIC = [
  { key: "diagram", label: "Diagramme", desc: "Infographies, schémas, flowcharts", icon: BarChart3, color: "bg-indigo-500/10 text-indigo-600" },
  { key: "data_bars", label: "Data viz", desc: "Barres de données horizontales", icon: BarChart3, color: "bg-emerald-500/10 text-emerald-600" },
  { key: "gradient_text", label: "Typo impact", desc: "Grand texte sur fond gradient", icon: Type, color: "bg-violet-500/10 text-violet-600" },
  { key: "timeline", label: "Timeline", desc: "Frise chronologique verticale", icon: GitBranch, color: "bg-sky-500/10 text-sky-600" },
  { key: "checklist", label: "Checklist", desc: "Liste à cocher visuelle", icon: ListChecks, color: "bg-teal-500/10 text-teal-600" },
] as const;

const VISUAL_FORMATS = [...VISUAL_FORMATS_PHOTO, ...VISUAL_FORMATS_GRAPHIC] as const;

const DAY_LABELS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const SLOT_HOURS = [9, 12, 15, 17, 19];

function computeWeeklyPlan(postsPerWeek: number, variants: string[], formats: string[]) {
  if (postsPerWeek === 0 || variants.length === 0 || formats.length === 0) return [];
  const daySpread = postsPerWeek <= 3
    ? [0, 2, 4].slice(0, postsPerWeek)
    : postsPerWeek <= 5
      ? [0, 1, 2, 3, 4].slice(0, postsPerWeek)
      : Array.from({ length: postsPerWeek }, (_, i) => i % 7);
  return daySpread.map((dayIdx, i) => ({
    day: DAY_LABELS[dayIdx],
    hour: SLOT_HOURS[i % SLOT_HOURS.length],
    variant: variants[i % variants.length],
    format: formats[i % formats.length],
  }));
}

// ─── Editable field helper ────────────────────────────────────────────────────

function BrandField({ label, value, onChange, placeholder, multiline }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string; multiline?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {multiline
        ? <Textarea value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="text-sm resize-none min-h-[60px]" />
        : <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="h-8 text-sm" />
      }
    </div>
  );
}

function TagListEditor({ label, tags, onChange, placeholder }: {
  label: string; tags: string[]; onChange: (tags: string[]) => void; placeholder?: string;
}) {
  const [input, setInput] = useState("");
  const addTag = () => {
    const t = input.trim();
    if (t && !tags.includes(t)) { onChange([...tags, t]); setInput(""); }
  };
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-xs text-muted-foreground">{label} ({tags.length})</Label>
      <div className="flex flex-wrap gap-1">
        {tags.map((tag, i) => (
          <Badge key={i} variant="outline" className="text-xs gap-1 pr-1">
            {tag}
            <button onClick={() => onChange(tags.filter((_, j) => j !== i))} className="ml-0.5 hover:text-destructive"><X className="h-2.5 w-2.5" /></button>
          </Badge>
        ))}
      </div>
      <div className="flex gap-1.5">
        <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder={placeholder} className="h-7 text-xs flex-1"
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }} />
        <Button variant="outline" size="sm" className="h-7 text-xs px-2" onClick={addTag} disabled={!input.trim()}>+</Button>
      </div>
    </div>
  );
}

// ─── Brand Identity Section ──────────────────────────────────────────────────

function BrandSection({ bc, updateProject }: {
  bc: Record<string, unknown>;
  updateProject: ReturnType<typeof useIgProject>["updateProject"];
}) {
  const [local, setLocal] = useState({
    name: String(bc.name || ""),
    domain: String(bc.domain || ""),
    tagline: String(bc.tagline || ""),
    industry: String(bc.industry || ""),
    tone: String(bc.tone || ""),
    cta_text: String(bc.cta_text || ""),
    language: String(bc.language || "fr"),
  });
  const [dirty, setDirty] = useState(false);

  const update = (key: string, val: string) => {
    setLocal((prev) => ({ ...prev, [key]: val }));
    setDirty(true);
  };

  const save = () => {
    updateProject({ brand_context: { ...bc, ...local } });
    setDirty(false);
    toast.success("Identité enregistrée");
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Identité de marque</p>
        {dirty && <Button size="sm" className="h-6 text-xs px-2" onClick={save}>Enregistrer</Button>}
      </div>
      <div className="flex flex-col gap-2.5">
        <BrandField label="Nom de la marque" value={local.name} onChange={(v) => update("name", v)} placeholder="Ex: Controly" />
        <BrandField label="Domaine" value={local.domain} onChange={(v) => update("domain", v)} placeholder="Ex: controly.ch" />
        <BrandField label="Tagline" value={local.tagline} onChange={(v) => update("tagline", v)} placeholder="Ex: Contrôle qualité chantier" />
        <BrandField label="Secteur / industrie" value={local.industry} onChange={(v) => update("industry", v)} placeholder="Ex: Construction / BTP" />
        <div className="flex gap-2">
          <div className="flex-1">
            <Label className="text-xs text-muted-foreground">Ton</Label>
            <Select value={local.tone || "professionnel"} onValueChange={(v) => update("tone", v)}>
              <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="professionnel">Professionnel</SelectItem>
                <SelectItem value="professionnel mais accessible">Pro & accessible</SelectItem>
                <SelectItem value="decontracte">Décontracté</SelectItem>
                <SelectItem value="expert et technique">Expert & technique</SelectItem>
                <SelectItem value="inspirant">Inspirant</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-20">
            <Label className="text-xs text-muted-foreground">Langue</Label>
            <Select value={local.language} onValueChange={(v) => update("language", v)}>
              <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="fr">FR</SelectItem>
                <SelectItem value="de">DE</SelectItem>
                <SelectItem value="en">EN</SelectItem>
                <SelectItem value="it">IT</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <BrandField label="CTA (call to action)" value={local.cta_text} onChange={(v) => update("cta_text", v)} placeholder="Ex: Demander une démo — lien en bio" />
      </div>
    </div>
  );
}

// ─── Colors Section ──────────────────────────────────────────────────────────

function ColorsSection({ bc, updateProject }: {
  bc: Record<string, unknown>;
  updateProject: ReturnType<typeof useIgProject>["updateProject"];
}) {
  const vi = (bc.visual_identity as Record<string, unknown>) || {};
  const [primary, setPrimary] = useState(String(vi.primary || "#1a1a2e"));
  const [accent, setAccent] = useState(String(vi.accent || "#e94560"));
  const [dirty, setDirty] = useState(false);

  const save = () => {
    updateProject({ brand_context: { ...bc, visual_identity: { ...vi, primary, accent } } });
    setDirty(false);
    toast.success("Couleurs enregistrées");
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Couleurs</p>
        {dirty && <Button size="sm" className="h-6 text-xs px-2" onClick={save}>Enregistrer</Button>}
      </div>
      <div className="flex gap-4">
        <div className="flex flex-col gap-1 flex-1">
          <Label className="text-xs text-muted-foreground">Primaire (fond)</Label>
          <div className="flex items-center gap-2">
            <input type="color" value={primary} onChange={(e) => { setPrimary(e.target.value); setDirty(true); }} className="w-8 h-8 rounded border border-border cursor-pointer" />
            <Input value={primary} onChange={(e) => { setPrimary(e.target.value); setDirty(true); }} className="h-8 text-xs font-mono flex-1" />
          </div>
        </div>
        <div className="flex flex-col gap-1 flex-1">
          <Label className="text-xs text-muted-foreground">Accent</Label>
          <div className="flex items-center gap-2">
            <input type="color" value={accent} onChange={(e) => { setAccent(e.target.value); setDirty(true); }} className="w-8 h-8 rounded border border-border cursor-pointer" />
            <Input value={accent} onChange={(e) => { setAccent(e.target.value); setDirty(true); }} className="h-8 text-xs font-mono flex-1" />
          </div>
        </div>
      </div>
      {/* Preview */}
      <div className="flex gap-2">
        <div className="flex-1 h-10 rounded-lg flex items-center justify-center text-xs font-medium" style={{ background: primary, color: "#fff" }}>Fond</div>
        <div className="flex-1 h-10 rounded-lg flex items-center justify-center text-xs font-medium" style={{ background: accent, color: "#fff" }}>Accent</div>
      </div>
    </div>
  );
}

// ─── Content Section (audience, pain_points, features) ───────────────────────

function ContentSection({ bc, updateProject }: {
  bc: Record<string, unknown>;
  updateProject: ReturnType<typeof useIgProject>["updateProject"];
}) {
  const [audience, setAudience] = useState(String(bc.audience || ""));
  const [audienceDirty, setAudienceDirty] = useState(false);
  const painPoints = Array.isArray(bc.pain_points) ? (bc.pain_points as string[]) : [];
  const features = Array.isArray(bc.features) ? (bc.features as string[]) : [];

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Contenu IA</p>
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between">
          <Label className="text-xs text-muted-foreground">Audience cible</Label>
          {audienceDirty && <Button size="sm" className="h-5 text-[10px] px-1.5" onClick={() => {
            updateProject({ brand_context: { ...bc, audience } });
            setAudienceDirty(false);
            toast.success("Audience enregistrée");
          }}>Sauver</Button>}
        </div>
        <Textarea value={audience} onChange={(e) => { setAudience(e.target.value); setAudienceDirty(true); }} placeholder="Décrivez votre audience cible..." className="text-sm resize-none min-h-[50px]" />
      </div>
      <TagListEditor
        label="Pain points clients"
        tags={painPoints}
        onChange={(tags) => { updateProject({ brand_context: { ...bc, pain_points: tags } }); toast.success("Pain points mis à jour"); }}
        placeholder="Ajouter un pain point..."
      />
      <TagListEditor
        label="Features à promouvoir"
        tags={features}
        onChange={(tags) => { updateProject({ brand_context: { ...bc, features: tags } }); toast.success("Features mises à jour"); }}
        placeholder="Ajouter une feature..."
      />
    </div>
  );
}

// ─── Hashtags Section ────────────────────────────────────────────────────────

function HashtagsSection({ hashtagConfig, updateProject }: {
  hashtagConfig: Record<string, unknown>;
  updateProject: ReturnType<typeof useIgProject>["updateProject"];
}) {
  const hc = hashtagConfig || {};
  const coreTags = Array.isArray(hc.core) ? (hc.core as string[]) : [];
  const poolTags = Array.isArray(hc.pool) ? (hc.pool as string[]) : [];
  const poolPick = (hc.pool_pick as number) || 9;
  const maxHashtags = (hc.max_hashtags as number) || 20;

  const save = (updates: Record<string, unknown>) => {
    updateProject({ hashtag_config: { ...hc, ...updates } });
  };

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Hashtags</p>
      <TagListEditor
        label="Core (toujours inclus)"
        tags={coreTags}
        onChange={(tags) => { save({ core: tags }); toast.success("Core hashtags mis à jour"); }}
        placeholder="#votrebrand"
      />
      <TagListEditor
        label="Pool (rotation aléatoire)"
        tags={poolTags}
        onChange={(tags) => { save({ pool: tags }); toast.success("Pool mis à jour"); }}
        placeholder="#hashtag"
      />
      <div className="flex gap-3">
        <div className="flex-1 flex items-center justify-between">
          <Label className="text-xs">Pool pick</Label>
          <Input type="number" className="w-16 h-7 text-xs" value={poolPick}
            onChange={(e) => { const v = parseInt(e.target.value); if (!isNaN(v) && v > 0 && v <= 25) save({ pool_pick: v }); }} />
        </div>
        <div className="flex-1 flex items-center justify-between">
          <Label className="text-xs">Max total</Label>
          <Input type="number" className="w-16 h-7 text-xs" value={maxHashtags}
            onChange={(e) => { const v = parseInt(e.target.value); if (!isNaN(v) && v > 0 && v <= 30) save({ max_hashtags: v }); }} />
        </div>
      </div>
    </div>
  );
}

// ─── Config Panel ─────────────────────────────────────────────────────────────

function ConfigPanel({ project, updateProject }: {
  project: NonNullable<ReturnType<typeof useIgProject>["project"]>;
  updateProject: ReturnType<typeof useIgProject>["updateProject"];
}) {
  const bc = project.brand_context as Record<string, unknown>;
  const contentVariants: string[] = Array.isArray(project.content_variants) ? project.content_variants : [];
  const visualFormats: string[] = Array.isArray(project.visual_formats) ? project.visual_formats : [];

  const toggleVariant = (key: string) => {
    const next = contentVariants.includes(key)
      ? contentVariants.filter((v) => v !== key)
      : [...contentVariants, key];
    updateProject({ content_variants: next });
  };

  const toggleFormat = (key: string) => {
    const next = visualFormats.includes(key)
      ? visualFormats.filter((v) => v !== key)
      : [...visualFormats, key];
    updateProject({ visual_formats: next });
  };

  const plan = computeWeeklyPlan(project.posts_per_week, contentVariants, visualFormats);
  const pipelineActive = !!bc.auto_generate || !!bc.auto_schedule || !!bc.auto_publish;
  const allAuto = !!bc.auto_generate && !!bc.auto_schedule && !!bc.auto_publish;

  const variantLabel = (key: string) => CONTENT_VARIANTS.find((v) => v.key === key)?.label ?? key;
  const formatLabel = (key: string) => VISUAL_FORMATS.find((f) => f.key === key)?.label ?? key;

  return (
    <TooltipProvider delayDuration={300}>
      <div className="flex flex-col gap-5 py-2">

        {/* ── Pipeline d'automatisation ── */}
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Pipeline d'automatisation</p>
            <Badge variant={allAuto ? "default" : pipelineActive ? "outline" : "secondary"} className="text-[10px]">
              {allAuto ? "100% auto" : pipelineActive ? "Partiel" : "Manuel"}
            </Badge>
          </div>
          <div className="rounded-xl border border-border bg-muted/30 p-3 flex flex-col gap-2.5">
            {PIPELINE_STEPS.map((step, idx) => (
              <div key={step.key}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className={`flex items-center justify-center w-7 h-7 rounded-lg bg-muted ${step.color}`}>
                      <step.icon className="h-3.5 w-3.5" />
                    </div>
                    <div>
                      <p className="text-sm font-medium leading-tight">{step.label}</p>
                      <p className="text-[10px] text-muted-foreground">{step.desc}</p>
                    </div>
                  </div>
                  <Switch
                    checked={!!bc[step.key]}
                    onCheckedChange={(v) => {
                      updateProject({ brand_context: { ...bc, [step.key]: v } });
                      toast.success(`${step.label} ${v ? "activé" : "désactivé"}`);
                    }}
                  />
                </div>
                {idx < PIPELINE_STEPS.length - 1 && (
                  <div className="flex justify-center py-1">
                    <div className="w-px h-3 bg-border" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <Separator />

        {/* ── Fréquence ── */}
        <div className="flex flex-col gap-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Fréquence</p>
          <div className="flex items-center justify-between">
            <Label className="text-sm">Publications / semaine</Label>
            <Select value={String(project.posts_per_week)} onValueChange={(v) => updateProject({ posts_per_week: Number(v) })}>
              <SelectTrigger className="w-20 h-8 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>{[1, 2, 3, 4, 5, 6, 7].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <Separator />

        {/* ── Variantes de contenu ── */}
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-1.5">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Variantes de contenu</p>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-3 w-3 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-52 text-xs">
                L'angle éditorial de chaque post. Les variantes sélectionnées tournent automatiquement.
              </TooltipContent>
            </Tooltip>
          </div>
          <div className="flex flex-col gap-1.5">
            {CONTENT_VARIANTS.map((v) => {
              const checked = contentVariants.includes(v.key);
              return (
                <button
                  key={v.key}
                  onClick={() => toggleVariant(v.key)}
                  className={`flex items-center gap-2.5 p-2 rounded-lg border transition-all text-left ${
                    checked ? "border-primary/30 bg-primary/5" : "border-transparent bg-muted/30 hover:bg-muted/50"
                  }`}
                >
                  <Checkbox checked={checked} className="pointer-events-none" />
                  <div className={`flex items-center justify-center w-6 h-6 rounded-md ${v.color}`}>
                    <v.icon className="h-3 w-3" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium leading-tight">{v.label}</p>
                    <p className="text-[10px] text-muted-foreground">{v.desc}</p>
                  </div>
                </button>
              );
            })}
          </div>
          {contentVariants.length === 0 && (
            <p className="text-[10px] text-amber-600 flex items-center gap-1"><Info className="h-3 w-3" />Sélectionnez au moins une variante.</p>
          )}
        </div>

        <Separator />

        {/* ── Formats visuels ── */}
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-1.5">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Formats visuels</p>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-3 w-3 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-52 text-xs">
                Le rendu graphique de chaque post. Les formats sélectionnés tournent automatiquement.
              </TooltipContent>
            </Tooltip>
          </div>
          {[
            { label: "Photo", formats: VISUAL_FORMATS_PHOTO, icon: Image },
            { label: "Graphique", formats: VISUAL_FORMATS_GRAPHIC, icon: BarChart3 },
          ].map((group) => (
            <div key={group.label} className="flex flex-col gap-1.5">
              <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5 mt-1">
                <group.icon className="h-3 w-3" />{group.label}
              </p>
              {group.formats.map((f) => {
                const checked = visualFormats.includes(f.key);
                return (
                  <button
                    key={f.key}
                    onClick={() => toggleFormat(f.key)}
                    className={`flex items-center gap-2.5 p-2 rounded-lg border transition-all text-left ${
                      checked ? "border-primary/30 bg-primary/5" : "border-transparent bg-muted/30 hover:bg-muted/50"
                    }`}
                  >
                    <Checkbox checked={checked} className="pointer-events-none" />
                    <div className={`flex items-center justify-center w-6 h-6 rounded-md ${f.color}`}>
                      <f.icon className="h-3 w-3" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium leading-tight">{f.label}</p>
                      <p className="text-[10px] text-muted-foreground">{f.desc}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          ))}
          {visualFormats.length === 0 && (
            <p className="text-[10px] text-amber-600 flex items-center gap-1"><Info className="h-3 w-3" />Sélectionnez au moins un format.</p>
          )}
        </div>

        <Separator />

        {/* ── Planning preview ── */}
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-1.5">
            <Eye className="h-3.5 w-3.5 text-muted-foreground" />
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Aperçu de la semaine</p>
          </div>
          {plan.length > 0 ? (
            <div className="rounded-xl border border-border bg-muted/30 overflow-hidden">
              {plan.map((slot, i) => {
                const vCfg = CONTENT_VARIANTS.find((v) => v.key === slot.variant);
                const fCfg = VISUAL_FORMATS.find((f) => f.key === slot.format);
                return (
                  <div key={i} className={`flex items-center gap-3 px-3 py-2 ${i > 0 ? "border-t border-border" : ""}`}>
                    <div className="flex flex-col items-center w-10 flex-shrink-0">
                      <span className="text-xs font-semibold">{slot.day}</span>
                      <span className="text-[10px] text-muted-foreground">{slot.hour}h</span>
                    </div>
                    <div className="flex-1 flex items-center gap-1.5 flex-wrap">
                      {vCfg && (
                        <Badge variant="outline" className={`text-[10px] ${vCfg.color}`}>
                          <vCfg.icon className="h-2.5 w-2.5 mr-0.5" />{vCfg.label}
                        </Badge>
                      )}
                      {fCfg && (
                        <Badge variant="outline" className={`text-[10px] ${fCfg.color}`}>
                          <fCfg.icon className="h-2.5 w-2.5 mr-0.5" />{fCfg.label}
                        </Badge>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-border p-4 text-center">
              <p className="text-xs text-muted-foreground">Configurez la fréquence, les variantes et les formats pour voir l'aperçu.</p>
            </div>
          )}
          {plan.length > 0 && contentVariants.length < project.posts_per_week && (
            <p className="text-[10px] text-muted-foreground flex items-center gap-1">
              <Info className="h-3 w-3" />
              {contentVariants.length} variante{contentVariants.length > 1 ? "s" : ""} pour {project.posts_per_week} posts — les variantes tournent en boucle.
            </p>
          )}
        </div>

        <Separator />

        {/* ── Identité de marque ── */}
        <BrandSection bc={bc} updateProject={updateProject} />

        <Separator />

        {/* ── Couleurs ── */}
        <ColorsSection bc={bc} updateProject={updateProject} />

        <Separator />

        {/* ── Contenu IA ── */}
        <ContentSection bc={bc} updateProject={updateProject} />

        <Separator />

        {/* ── Hashtags ── */}
        <HashtagsSection hashtagConfig={project.hashtag_config as Record<string, unknown>} updateProject={updateProject} />

        <Separator />

        {/* ── Compte ── */}
        <div className="flex flex-col gap-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Compte</p>
          <div className="flex items-center justify-between">
            <Label className="text-sm">Compte actif</Label>
            <Switch checked={project.is_active} onCheckedChange={(v) => {
              if (!v && !window.confirm("Désactiver le compte ? Tous les crons (génération, publication, analytics) seront arrêtés.")) return;
              updateProject({ is_active: v });
              toast.success(v ? "Compte activé" : "Compte désactivé");
            }} />
          </div>
          <div><Label className="text-xs text-muted-foreground">Username</Label><p className="text-sm">@{project.ig_username}</p></div>
          <div><Label className="text-xs text-muted-foreground">Timezone</Label><p className="text-sm">{project.timezone}</p></div>
        </div>
      </div>
    </TooltipProvider>
  );
}

// ─── Post Card ────────────────────────────────────────────────────────────────

function ScheduledCard({ post, onSelect, onPublishNow, onUnschedule, onDelete, onTimeChange }: {
  post: IgQueuePost;
  onSelect: () => void;
  onPublishNow: (id: string) => Promise<unknown>;
  onUnschedule: (id: string) => Promise<void>;
  onDelete: (id: string) => Promise<boolean>;
  onTimeChange: (id: string, t: string) => Promise<void>;
}) {
  const [publishing, setPublishing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const scheduledDate = post.scheduled_at ? new Date(post.scheduled_at) : null;
  const [timeVal, setTimeVal] = useState(() => scheduledDate ? format(scheduledDate, "HH:mm") : "");
  const timeChangeRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handlePublish = async () => {
    setPublishing(true);
    await onPublishNow(post.id);
    setPublishing(false);
  };

  return (
    <div className="flex items-start gap-3 p-2.5 rounded-xl border border-border bg-card hover:bg-muted/30 transition-colors group">
      <Thumb url={post.image_url} onClick={onSelect} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{getHeadline(post)}</p>
        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
          <CBadge c={post.category} />
          {post.media_type === "carousel" && Array.isArray(post.image_urls) && post.image_urls.length >= 2 && <Badge variant="outline" className="text-[10px] bg-orange-500/10 text-orange-600"><LayoutGrid className="h-2.5 w-2.5 mr-0.5" />{post.image_urls.length}</Badge>}
          {scheduledDate && (
            <span className="text-xs text-blue-600 font-medium">
              {format(scheduledDate, "EEE H'h'mm", { locale: fr })}
            </span>
          )}
          {scheduledDate && (
            <span className="text-[10px] text-muted-foreground">
              {formatDistanceToNow(scheduledDate, { locale: fr, addSuffix: true })}
            </span>
          )}
        </div>
        <p className="text-[10px] text-muted-foreground/60 mt-0.5">
          Généré le {format(new Date(post.created_at), "d MMM à H'h'mm", { locale: fr })}
          {post.generation_metadata && (post.generation_metadata as Record<string, unknown>).composition
            ? ` · ${(post.generation_metadata as Record<string, unknown>).composition}`
            : ""}
        </p>
        <Input
          type="time"
          value={timeVal}
          onChange={(e) => {
            setTimeVal(e.target.value);
            if (timeChangeRef.current) clearTimeout(timeChangeRef.current);
            const val = e.target.value;
            timeChangeRef.current = setTimeout(() => onTimeChange(post.id, val), 500);
          }}
          className="h-6 w-24 text-[10px] px-1 py-0 mt-1 font-mono text-muted-foreground"
        />
      </div>
      <div className="flex flex-col items-end gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button variant="ghost" size="sm" className="h-7 text-xs text-blue-600 hover:text-blue-700" onClick={handlePublish} disabled={publishing}>
          {publishing ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Send className="h-3 w-3 mr-1" />Publier</>}
        </Button>
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground" onClick={() => onUnschedule(post.id)} title="Dé-planifier">
            <X className="h-3.5 w-3.5" />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={async () => { if (!window.confirm("Supprimer ce post ?")) return; setDeleting(true); await onDelete(post.id); setDeleting(false); }} disabled={deleting}>
            {deleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
          </Button>
        </div>
      </div>
    </div>
  );
}

function PublishedCard({ post, onSelect, publishTo }: {
  post: IgPublishedPost;
  onSelect: () => void;
  publishTo: string[];
}) {
  const hasIg = !!post.ig_media_id;
  const hasFb = !!post.fb_post_id;
  const wantsFb = publishTo.includes("facebook");

  return (
    <div className="flex items-center gap-3 p-2.5 rounded-xl border border-border bg-card hover:bg-muted/30 transition-colors group">
      <Thumb url={post.image_url} onClick={onSelect} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{getHeadline(post)}</p>
        <p className="text-[10px] text-muted-foreground">{format(new Date(post.published_at), "d MMM à H'h'mm", { locale: fr })}</p>
        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
          {publishTo.includes("instagram") && <PlatResult label="IG" ok={hasIg} />}
          {wantsFb && <PlatResult label="FB" ok={hasFb} />}
          {post.ig_analytics && (
            <span className="text-[10px] text-muted-foreground flex items-center gap-1">
              <Heart className="h-3 w-3" />{post.ig_analytics.likes ?? 0}
              <MessageCircle className="h-3 w-3 ml-1" />{post.ig_analytics.comments ?? 0}
              <Badge variant="outline" className="text-[9px] ml-1 text-emerald-600 py-0">{(post.ig_analytics.engagement_score ?? 0).toFixed(1)}%</Badge>
            </span>
          )}
        </div>
      </div>
      <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
        {post.ig_permalink && (
          <a href={post.ig_permalink} target="_blank" rel="noreferrer">
            <Button variant="ghost" size="icon" className="h-7 w-7"><ExternalLink className="h-3.5 w-3.5" /></Button>
          </a>
        )}
      </div>
    </div>
  );
}

// ─── Column wrapper ───────────────────────────────────────────────────────────

function Column({ accent, icon, title, count, toggle, toggleLabel, toggleValue, onToggle, footer, children }: {
  accent: string;
  icon: React.ReactNode;
  title: string;
  count: number;
  toggle?: React.ReactNode;
  toggleLabel?: string;
  toggleValue?: boolean;
  onToggle?: () => void;
  footer?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col rounded-2xl border border-border bg-muted/20 overflow-hidden" style={{ minHeight: 0 }}>
      {/* Column header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-card sticky top-0 z-10">
        <div className="flex items-center gap-2">
          <span className={accent}>{icon}</span>
          <span className="font-semibold text-sm">{title}</span>
          <Badge variant="outline" className="text-xs h-5">{count}</Badge>
        </div>
        <div className="flex items-center gap-2">
          {toggleLabel && onToggle !== undefined && (
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-muted-foreground">{toggleLabel}</span>
              <Switch checked={!!toggleValue} onCheckedChange={onToggle} className="scale-75" />
            </div>
          )}
          {toggle}
        </div>
      </div>
      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto p-3 flex flex-col gap-2" style={{ maxHeight: "calc(100vh - 260px)" }}>
        {children}
      </div>
      {/* Footer */}
      {footer && (
        <div className="border-t border-border px-4 py-2.5 bg-card text-xs text-muted-foreground">
          {footer}
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function AdminMarketing() {
  const projectSlug = useProjectSlug();
  const navigate = useNavigate();

  const {
    posts, loading: qL,
    triggerGenerate, triggerPublish,
    updateCaption, deleteItem, regenerateImage, updateItem,
  } = useIgQueue(projectSlug);

  const { posts: published, loading: pubL } = useIgPublished(20, projectSlug);
  const { project, loading: projL, updateProject } = useIgProject(projectSlug);

  const [selectedQueue, setSelectedQueue] = useState<IgQueuePost | null>(null);
  const [selectedPublished, setSelectedPublished] = useState<IgPublishedPost | null>(null);
  const [generating, setGenerating] = useState(false);
  const [publishingAll, setPublishingAll] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const [genDialogOpen, setGenDialogOpen] = useState(false);
  const [genCategory, setGenCategory] = useState<string>("auto");
  const [genFormat, setGenFormat] = useState<string>("auto");

  const loading = qL || pubL || projL;
  const publishTo = project?.publish_to ?? ["instagram"];
  const bc = (project?.brand_context ?? {}) as Record<string, unknown>;

  // Column data
  const unscheduledReadyPosts = posts.filter((p) => p.status === "ready" && !p.scheduled_at && p.image_url);
  const failedPosts = posts.filter((p) => p.status === "failed");
  const scheduledPosts = posts
    .filter((p) => p.status === "ready" && p.scheduled_at)
    .sort((a, b) => new Date(a.scheduled_at!).getTime() - new Date(b.scheduled_at!).getTime());

  // Toggle helpers
  const handleToggle = useCallback((key: "auto_generate" | "auto_schedule" | "auto_publish") => {
    if (!project) return;
    updateProject({ brand_context: { ...bc, [key]: !bc[key] } });
  }, [project, bc, updateProject]);

  const handleTimeChange = useCallback(async (postId: string, t: string) => {
    const post = scheduledPosts.find((p) => p.id === postId);
    if (!post || !post.scheduled_at) return;
    const [h, m] = t.split(":").map(Number);
    if (isNaN(h) || isNaN(m) || h < 0 || h > 23 || m < 0 || m > 59) return;
    const base = startOfDay(new Date(post.scheduled_at));
    base.setHours(h, m, 0, 0);
    await updateItem(postId, { scheduled_at: base.toISOString() });
  }, [scheduledPosts, updateItem]);

  const handleGenerate = async (n: number, category?: string, visualFormat?: string) => {
    setGenerating(true);
    setGenDialogOpen(false);
    try {
      const opts: Record<string, string> = {};
      if (category && category !== "auto") opts.category = category;
      if (visualFormat && visualFormat !== "auto") opts.visual_format = visualFormat;
      for (let i = 0; i < n; i++) await triggerGenerate(Object.keys(opts).length > 0 ? opts : undefined);
      toast.success(`${n} post${n > 1 ? "s" : ""} généré${n > 1 ? "s" : ""}`);
    } catch { toast.error("Erreur lors de la génération"); }
    setGenerating(false);
  };

  const handlePublishAll = async () => {
    setPublishingAll(true);
    try {
      await triggerPublish();
      toast.success("Publication lancée");
    } catch { toast.error("Erreur lors de la publication"); }
    setPublishingAll(false);
  };

  if (loading) {
    return (
      <div className="container mx-auto py-6 px-4 space-y-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 px-4 space-y-6">
      <div className="flex flex-col gap-5 p-4 sm:p-6 max-w-7xl mx-auto w-full">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin')} aria-label="Retour">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center justify-center w-9 h-9 rounded-xl text-white flex-shrink-0" style={{ background: IGGradient }}>
              <Instagram className="h-5 w-5" />
            </div>
            <div>
              <p className="font-semibold text-base leading-tight">
                {project ? `@${project.ig_username}` : "Instagram Pipeline"}
              </p>
              <p className="text-xs text-muted-foreground">
              Pipeline de publication
              {project && (() => {
                const b = (project.brand_context ?? {}) as Record<string, unknown>;
                const on = [b.auto_generate && "Gen", b.auto_schedule && "Sched", b.auto_publish && "Pub"].filter(Boolean);
                return on.length > 0
                  ? <span className="ml-1.5 text-emerald-600 font-medium">· {on.join(" → ")}</span>
                  : <span className="ml-1.5 text-muted-foreground/50">· Manuel</span>;
              })()}
            </p>
            </div>
          </div>
          <Dialog open={configOpen} onOpenChange={setConfigOpen}>
            <Button variant="outline" size="sm" className="gap-2" onClick={() => setConfigOpen(true)}>
              <Settings className="h-4 w-4" />Config
            </Button>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Configuration</DialogTitle></DialogHeader>
              {project && <ConfigPanel project={project} updateProject={updateProject} />}
            </DialogContent>
          </Dialog>
        </div>

        {/* 2-Column Pipeline */}
        <div className="grid grid-cols-1 md:grid-cols-[55%_45%] gap-4">

          {/* Column 1: PLANIFIÉ */}
          <Column
            accent="text-blue-500"
            icon={<Calendar className="h-4 w-4" />}
            title="Planifié"
            count={scheduledPosts.length}
            toggleLabel="Auto"
            toggleValue={!!bc.auto_schedule}
            onToggle={() => handleToggle("auto_schedule")}
            toggle={
              <div className="flex items-center gap-2">
                <Button size="sm" className="h-7 text-xs gap-1 bg-purple-600 hover:bg-purple-700 text-white" disabled={generating} onClick={() => setGenDialogOpen(true)}>
                  {generating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                  Générer
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs gap-1"
                  onClick={handlePublishAll}
                  disabled={publishingAll || scheduledPosts.length === 0}
                >
                  {publishingAll ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                  Tout publier
                </Button>
              </div>
            }
            footer={<span>{scheduledPosts.length} posts planifiés</span>}
          >
            {scheduledPosts.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-muted-foreground text-center">
                <Calendar className="h-6 w-6 mb-2 opacity-30" />
                <p className="text-xs">Aucun post planifié</p>
              </div>
            ) : (
              scheduledPosts.map((post) => (
                <ScheduledCard
                  key={post.id}
                  post={post}
                  onSelect={() => setSelectedQueue(post)}
                  onPublishNow={triggerPublish}
                  onUnschedule={async (id) => { await updateItem(id, { scheduled_at: null as unknown as string }); }}
                  onDelete={deleteItem}
                  onTimeChange={handleTimeChange}
                />
              ))
            )}
            {unscheduledReadyPosts.length > 0 && (
              <div className="border-t border-border pt-2 mt-2">
                <p className="text-xs text-muted-foreground mb-2">En attente de planification</p>
                {unscheduledReadyPosts.map((post) => (
                  <ScheduledCard
                    key={post.id}
                    post={post}
                    onSelect={() => setSelectedQueue(post)}
                    onPublishNow={triggerPublish}
                    onUnschedule={async () => {}}
                    onDelete={deleteItem}
                    onTimeChange={handleTimeChange}
                  />
                ))}
              </div>
            )}
            {failedPosts.length > 0 && (
              <div className="border-t border-destructive/20 pt-2 mt-2">
                <p className="text-xs font-semibold text-destructive mb-2">Échecs ({failedPosts.length})</p>
                {failedPosts.map((post) => (
                  <div key={post.id} className="flex items-center gap-2 p-2 rounded-lg bg-destructive/5 border border-destructive/20 mb-1.5">
                    <div className="w-10 h-10 rounded bg-muted flex items-center justify-center flex-shrink-0">
                      {post.image_url ? <img src={post.image_url} className="w-full h-full rounded object-cover" /> : <X className="h-4 w-4 text-destructive" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-destructive truncate">{(post.generation_metadata as Record<string,unknown>)?.headline as string || post.template_id}</p>
                      <p className="text-[10px] text-destructive/70 truncate">{post.error_message || "Erreur inconnue"}</p>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-600 hover:text-blue-700" title="Réessayer" onClick={async () => { toast.info("Régénération..."); await triggerGenerate(post.template_id ? { template_id: post.template_id } : undefined); await deleteItem(post.id); toast.success("Post régénéré"); }}><RotateCcw className="h-3.5 w-3.5" /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" title="Supprimer" onClick={() => deleteItem(post.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Column>

          {/* Column 2: PUBLIÉ */}
          <Column
            accent="text-emerald-500"
            icon={<CheckCircle className="h-4 w-4" />}
            title="Publié"
            count={published.length}
            footer={<span>{published.length} posts publiés</span>}
          >
            {published.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-muted-foreground text-center">
                <CheckCircle2 className="h-6 w-6 mb-2 opacity-30" />
                <p className="text-xs">Aucun post publié</p>
              </div>
            ) : (
              published.map((post) => (
                <PublishedCard
                  key={post.id}
                  post={post}
                  onSelect={() => setSelectedPublished(post)}
                  publishTo={publishTo}
                />
              ))
            )}
          </Column>
        </div>
      </div>

      {/* Post Detail Dialog — queue (scheduled) */}
      <PostDetailDialog
        post={selectedQueue}
        publishedPost={null}
        onClose={() => setSelectedQueue(null)}
        onCaptionSave={updateCaption}
        onPublish={triggerPublish}
        onRegenerate={regenerateImage}
        onDelete={deleteItem}
        onSchedule={async (id) => { await updateItem(id, { scheduled_at: new Date(Date.now() + 86400000).toISOString() }); }}
        onUnschedule={async (id) => { await updateItem(id, { scheduled_at: null as unknown as string }); }}
        publishTo={publishTo}
      />

      {/* Post Detail Dialog — published */}
      <PostDetailDialog
        post={null}
        publishedPost={selectedPublished}
        onClose={() => setSelectedPublished(null)}
        publishTo={publishTo}
      />

      {/* Generate Dialog */}
      <Dialog open={genDialogOpen} onOpenChange={setGenDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-purple-500" />
              Générer un post
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label className="text-xs font-medium">Catégorie</Label>
              <Select value={genCategory} onValueChange={setGenCategory}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto (rotation)</SelectItem>
                  <SelectItem value="pain_point">Pain point</SelectItem>
                  <SelectItem value="feature">Feature</SelectItem>
                  <SelectItem value="social_proof">Social proof</SelectItem>
                  <SelectItem value="educational">Educational</SelectItem>
                  <SelectItem value="content">Contenu</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-medium">Format visuel</Label>
              <Select value={genFormat} onValueChange={setGenFormat}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto (rotation)</SelectItem>
                  {(project?.visual_formats || ["simple", "carousel", "diagram", "citation", "before_after"]).map((f) => (
                    <SelectItem key={f} value={f}>{f === "screenshot" ? "Screenshot app" : f === "before_after" ? "Avant / Après" : f.charAt(0).toUpperCase() + f.slice(1)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Separator />
            <div className="flex gap-2">
              <Button className="flex-1 h-9 text-xs bg-purple-600 hover:bg-purple-700" disabled={generating} onClick={() => handleGenerate(1, genCategory, genFormat)}>
                {generating ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Sparkles className="h-3 w-3 mr-1" />}
                1 post
              </Button>
              <Button variant="outline" className="flex-1 h-9 text-xs" disabled={generating} onClick={() => handleGenerate(3, genCategory, genFormat)}>3 posts</Button>
              <Button variant="outline" className="flex-1 h-9 text-xs" disabled={generating} onClick={() => handleGenerate(5, genCategory, genFormat)}>5 posts</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
